import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Leaf, Droplets, Thermometer, Bug, CloudRain,
  Sun, Calendar, AlertTriangle, Sprout
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GardeningForecast({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGardeningData();
  }, [lat, lon]);

  const fetchGardeningData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/gardening`, {
        params: { lat, lon }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch gardening data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-48 mb-4"></div>
        <div className="h-48 bg-white/10 rounded"></div>
      </div>
    );
  }

  if (!data?.success) {
    return null;
  }

  const { zone, current_conditions, recommendations, risks, forecast, tips } = data;

  const getRiskColor = (risk) => {
    if (risk === "Low") return "text-green-400";
    if (risk === "Moderate") return "text-yellow-400";
    return "text-red-400";
  };

  const getIndexColor = (index) => {
    if (index >= 8) return "text-green-400";
    if (index >= 5) return "text-emerald-400";
    if (index >= 3) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
            <Leaf className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
          </div>
          <div>
            <h3 className="font-bold">Gardening Forecast</h3>
            <p className="text-sm opacity-60">USDA Zone {zone.usda_zone}</p>
          </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full bg-green-500/10 text-green-300 border border-green-500/20" data-testid="gardening-source-comparison-badge">
          Weather + seasonal model
        </span>
      </div>

      {/* Zone Info */}
      <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs opacity-60 mb-1">Last Frost</p>
            <p className="text-sm font-medium">{zone.last_frost_date}</p>
          </div>
          <div>
            <p className="text-xs opacity-60 mb-1">First Frost</p>
            <p className="text-sm font-medium">{zone.first_frost_date}</p>
          </div>
          <div>
            <p className="text-xs opacity-60 mb-1">Growing Season</p>
            <p className="text-sm font-medium">{zone.growing_season_days} days</p>
          </div>
        </div>
      </div>

      {/* Current Conditions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Thermometer className="w-5 h-5 mx-auto mb-1 text-orange-400" />
          <p className="text-xs opacity-60">Air Temp</p>
          <p className="text-xl font-bold">{current_conditions.air_temperature}°F</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Sprout className="w-5 h-5 mx-auto mb-1 text-amber-500" />
          <p className="text-xs opacity-60">Soil Temp</p>
          <p className="text-xl font-bold">{current_conditions.soil_temperature}°F</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Droplets className="w-5 h-5 mx-auto mb-1 text-blue-400" />
          <p className="text-xs opacity-60">Soil Moisture</p>
          <p className="text-sm font-bold">{current_conditions.soil_moisture}</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Sun className="w-5 h-5 mx-auto mb-1 text-yellow-400" />
          <p className="text-xs opacity-60">GDD Today</p>
          <p className="text-xl font-bold">{current_conditions.growing_degree_days_today}</p>
        </div>
      </div>

      {/* Risks */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Thermometer className="w-4 h-4 mx-auto mb-1 text-cyan-400" />
          <p className="text-xs opacity-60">Frost Risk</p>
          <p className={`font-bold ${getRiskColor(risks.frost_risk)}`}>{risks.frost_risk}</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <Bug className="w-4 h-4 mx-auto mb-1 text-orange-400" />
          <p className="text-xs opacity-60">Pest Risk</p>
          <p className={`font-bold ${getRiskColor(risks.pest_risk)}`}>{risks.pest_risk}</p>
        </div>
        
        <div className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
          <AlertTriangle className="w-4 h-4 mx-auto mb-1 text-red-400" />
          <p className="text-xs opacity-60">Disease Risk</p>
          <p className={`font-bold ${getRiskColor(risks.disease_risk)}`}>{risks.disease_risk}</p>
        </div>
      </div>

      {/* Recommendations */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
          <h4 className="text-sm font-semibold mb-2 text-green-500 flex items-center gap-2">
            <Sprout className="w-4 h-4" />
            Plant Now
          </h4>
          <ul className="space-y-1">
            {recommendations.planting_now.slice(0, 4).map((item, idx) => (
              <li key={`item-${idx}`} className="text-sm opacity-80">• {item}</li>
            ))}
          </ul>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
          <h4 className="text-sm font-semibold mb-2 text-amber-500 flex items-center gap-2">
            <Leaf className="w-4 h-4" />
            Harvest Soon
          </h4>
          <ul className="space-y-1">
            {recommendations.harvest_soon.slice(0, 4).map((item, idx) => (
              <li key={`item-${idx}`} className="text-sm opacity-80">• {item}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Watering Recommendation */}
      <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
        <div className="flex items-center gap-2 mb-1">
          <Droplets className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-blue-500">Watering Recommendation</span>
        </div>
        <p className="text-sm opacity-80">{recommendations.watering}</p>
        <p className="text-xs opacity-50 mt-1">Best planting time: {recommendations.best_planting_time}</p>
      </div>

      {/* 5-Day Garden Forecast */}
      <div>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 opacity-60" />
          5-Day Garden Forecast
        </h4>
        <div className="grid grid-cols-5 gap-2">
          {forecast.map((day, idx) => (
            <div 
              key={`item-${idx}`}
              className={`p-3 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
            >
              <p className="text-xs font-medium">{idx === 0 ? 'Today' : day.day.slice(0, 3)}</p>
              <p className="text-sm opacity-60">{day.date}</p>
              <div className="my-2">
                {day.rain_chance > 50 ? (
                  <CloudRain className="w-5 h-5 mx-auto text-blue-400" />
                ) : (
                  <Sun className="w-5 h-5 mx-auto text-yellow-400" />
                )}
              </div>
              <p className="text-xs">
                <span className="text-red-400">{day.high_temp}°</span>
                <span className="opacity-50"> / </span>
                <span className="text-blue-400">{day.low_temp}°</span>
              </p>
              <p className={`text-xs mt-1 font-medium ${getIndexColor(day.gardening_index)}`}>
                {day.gardening_index}/10
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className={`mt-4 p-3 rounded-xl ${isDarkMode ? 'bg-green-500/5' : 'bg-green-50/50'}`}>
        <p className="text-xs font-medium text-green-500 mb-2">Garden Tips</p>
        <p className="text-[10px] opacity-50 mb-2" data-testid="gardening-source-comparison-note">
          Planting, frost, pest, and disease guidance cross-checks live weather inputs against USDA-zone seasonal rules.
        </p>
        <ul className="space-y-1">
          {tips.map((tip, idx) => (
            <li key={`item-${idx}`} className="text-xs opacity-70">💡 {tip}</li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}
